/**
 * Presentation helpers shared by the Player Health list and device detail
 * pages. Pure functions only; no React.
 */
import type { PlayerHealthDevice, PlayerStatus, PlayerTelemetryEvent } from '../../api/types';

export const PLAYER_STATUS_COLORS: Record<PlayerStatus, { bg: string; color: string }> = {
  alive: { bg: '#10b98120', color: '#10b981' },
  stale: { bg: '#f59e0b20', color: '#f59e0b' },
  dead: { bg: '#ef444420', color: '#ef4444' },
};

export const PLAYER_STATUS_LABELS: Record<PlayerStatus, string> = {
  alive: 'Alive',
  stale: 'Stale',
  dead: 'Dead',
};

export const EVENT_TYPE_COLORS: Record<string, string> = {
  error: '#ef4444',
  audio_error: '#ef4444',
  state_change: '#6366f1',
  lifecycle: '#0ea5e9',
  heartbeat: '#6b7280',
};

export function formatSecondsAgo(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return 'Never';
  if (seconds < 5) return 'Just now';
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '-';
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export function formatTrackPosition(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '';
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '-';
  // D1 CURRENT_TIMESTAMP rows come back as 'YYYY-MM-DD HH:MM:SS' in UTC.
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? value.replace(' ', 'T') + 'Z'
    : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function shortVersion(version: string | null | undefined): string {
  if (!version) return '-';
  return version.length > 12 ? version.slice(0, 7) : version;
}

export function trackLabel(device: Pick<PlayerHealthDevice, 'current_track_id' | 'current_track_title' | 'current_track_artist'>): string {
  if (device.current_track_title) {
    return device.current_track_artist
      ? `${device.current_track_title} - ${device.current_track_artist}`
      : device.current_track_title;
  }
  if (device.current_track_id) return device.current_track_id;
  return '';
}

export function deviceLabel(device: Pick<PlayerHealthDevice, 'device_id' | 'zone_name' | 'site_name'>): string {
  if (device.zone_name && device.site_name) return `${device.site_name} / ${device.zone_name}`;
  if (device.zone_name) return device.zone_name;
  if (device.site_name) return device.site_name;
  return device.device_id.slice(0, 8);
}

/** "Site / Organization" caption, printed once when the two share a name. */
export function venueCaption(device: Pick<PlayerHealthDevice, 'site_name' | 'organization_name'>): string {
  const parts = [device.site_name, device.organization_name].filter(Boolean) as string[];
  return Array.from(new Set(parts)).join(' / ');
}

export const REQUIREMENT_FAILURE_LABELS: Record<string, string> = {
  'mobile-browser-tab': 'Running in a mobile browser tab (not installed)',
  'no-offline-cache': 'No offline cache available',
  'no-wake-lock': 'No screen wake lock available',
};

export function describeFailure(code: string): string {
  return REQUIREMENT_FAILURE_LABELS[code] || code;
}

/** Human summary of one event row for the timeline. */
export function eventSummary(event: PlayerTelemetryEvent): string {
  const data = event.event_data || {};
  switch (event.event_type) {
    case 'error':
    case 'audio_error': {
      const kind = typeof data.error_kind === 'string' ? data.error_kind : event.event_type;
      const message = typeof data.message === 'string' ? data.message : '';
      return message ? `${kind}: ${message}` : kind;
    }
    case 'state_change': {
      const change = typeof data.change === 'string' ? data.change : 'state change';
      if (change === 'track_change') {
        return `Track change ${data.previous_track_id ? `from ${String(data.previous_track_id).slice(0, 8)} ` : ''}to ${String(data.current_track_id ?? '').slice(0, 8)}`;
      }
      return change === 'play' ? 'Playback started' : change === 'pause' ? 'Playback paused' : change;
    }
    case 'lifecycle': {
      const change = typeof data.change === 'string' ? data.change : 'lifecycle';
      const labels: Record<string, string> = {
        hidden: 'Page hidden',
        visible: 'Page visible',
        online: 'Network online',
        offline: 'Network offline',
        session_start: 'Session started',
        session_end: 'Session ended',
      };
      return labels[change] || change;
    }
    default:
      return event.event_type;
  }
}

/** Clipboard-friendly markdown for one error event, for pasting into a triage thread. */
export function errorToMarkdown(event: PlayerTelemetryEvent, device: Pick<PlayerHealthDevice, 'device_id' | 'zone_name' | 'site_name' | 'user_email'>): string {
  const data = event.event_data || {};
  const lines: string[] = [];
  const kind = typeof data.error_kind === 'string' ? data.error_kind : event.event_type;
  lines.push(`**Player error** \`${kind}\` - ${typeof data.message === 'string' ? data.message : '(no message)'}`);
  lines.push(`- device: \`${device.device_id}\``);
  lines.push(`- venue: ${deviceLabel(device)}`);
  if (device.user_email) lines.push(`- user: ${device.user_email}`);
  lines.push(`- version: ${event.app_version || '-'}`);
  lines.push(`- server time: ${formatTimestamp(event.created_at)}`);
  if (typeof data.client_time === 'number') lines.push(`- client time: ${new Date(data.client_time).toISOString()}`);
  if (typeof data.error_name === 'string') lines.push(`- error name: ${data.error_name}`);
  if (typeof data.stack === 'string' && data.stack) {
    lines.push('');
    lines.push('stack:');
    lines.push('```');
    lines.push(data.stack);
    lines.push('```');
  }
  const { error_kind, message, stack, error_name, client_time, ...rest } = data;
  if (Object.keys(rest).length > 0) {
    lines.push('');
    lines.push('context:');
    lines.push('```json');
    lines.push(JSON.stringify(rest, null, 2));
    lines.push('```');
  }
  return lines.join('\n');
}

// ---- Fleet rollups (step 0.9: status dots on org / site / zone rows) ----

export type RollupStatus = PlayerStatus | 'none';

export interface PlayerRollup {
  total: number;
  alive: number;
  stale: number;
  dead: number;
  /** Spec: red when any player is dead, amber when any is stale, green when all alive. */
  status: RollupStatus;
  /** The most recently seen player in the group, for "now playing" / "last seen" inline text. */
  latest: PlayerHealthDevice | null;
}

export interface PlayerHealthRollups {
  byOrganization: Map<string, PlayerRollup>;
  bySite: Map<string, PlayerRollup>;
  byZone: Map<string, PlayerRollup>;
}

export const ROLLUP_COLORS: Record<RollupStatus, string> = {
  alive: PLAYER_STATUS_COLORS.alive.color,
  stale: PLAYER_STATUS_COLORS.stale.color,
  dead: PLAYER_STATUS_COLORS.dead.color,
  none: '#6b7280',
};

function emptyRollup(): PlayerRollup {
  return { total: 0, alive: 0, stale: 0, dead: 0, status: 'none', latest: null };
}

function addToRollup(map: Map<string, PlayerRollup>, key: string | null, device: PlayerHealthDevice) {
  if (!key) return;
  const r = map.get(key) || emptyRollup();
  r.total++;
  r[device.status]++;
  const seen = device.last_seen_seconds_ago ?? Infinity;
  const latestSeen = r.latest?.last_seen_seconds_ago ?? Infinity;
  if (!r.latest || seen < latestSeen) r.latest = device;
  map.set(key, r);
}

function finalize(map: Map<string, PlayerRollup>) {
  map.forEach((r) => {
    r.status = r.dead > 0 ? 'dead' : r.stale > 0 ? 'stale' : r.alive > 0 ? 'alive' : 'none';
  });
}

/** Pure: group a player list by organization, site and zone. */
export function rollupPlayerHealth(devices: PlayerHealthDevice[]): PlayerHealthRollups {
  const byOrganization = new Map<string, PlayerRollup>();
  const bySite = new Map<string, PlayerRollup>();
  const byZone = new Map<string, PlayerRollup>();
  for (const d of devices) {
    addToRollup(byOrganization, d.organization_id, d);
    addToRollup(bySite, d.site_id, d);
    addToRollup(byZone, d.zone_id, d);
  }
  finalize(byOrganization);
  finalize(bySite);
  finalize(byZone);
  return { byOrganization, bySite, byZone };
}

export function rollupSummary(r: PlayerRollup | undefined): string {
  if (!r || r.total === 0) return 'No player has reported from here';
  const parts: string[] = [];
  if (r.alive) parts.push(`${r.alive} alive`);
  if (r.stale) parts.push(`${r.stale} stale`);
  if (r.dead) parts.push(`${r.dead} dead`);
  return `${r.total} player${r.total === 1 ? '' : 's'}: ${parts.join(', ')}`;
}
